<script setup lang="ts">
import { computed } from "vue";

import type { AbilityAnswer, OptionItem, PairOptions, Question, StrongAbility } from "../types";

const props = defineProps<{
  question: Question;
  modelValue: unknown;
}>();
const emit = defineEmits<{ "update:modelValue": [value: unknown] }>();

const listOptions = computed(() =>
  Array.isArray(props.question.options) ? props.question.options : [],
);
const pairOptions = computed(() =>
  Array.isArray(props.question.options) ? null : (props.question.options as PairOptions),
);
const selectedValues = computed<string[]>({
  get: () => (Array.isArray(props.modelValue) ? (props.modelValue as string[]) : []),
  set: (value) => emit("update:modelValue", value),
});
const scalarValue = computed<number | undefined>({
  get: () => (typeof props.modelValue === "number" ? props.modelValue : undefined),
  set: (value) => emit("update:modelValue", value),
});
const choiceValue = computed<string | undefined>({
  get: () => (typeof props.modelValue === "string" ? props.modelValue : undefined),
  set: (value) => emit("update:modelValue", value),
});
const scaleLabels = computed(() => pairOptions.value?.labels || []);
const hasPair = computed(() => Boolean(pairOptions.value?.left && pairOptions.value?.right));
const scaleStartLabel = computed(() =>
  hasPair.value ? "偏向 A" : scaleLabels.value[0] || "",
);
const scaleEndLabel = computed(() =>
  hasPair.value ? "偏向 B" : scaleLabels.value[scaleLabels.value.length - 1] || "",
);
const selectedScaleLabel = computed(() =>
  scalarValue.value ? scaleLabels.value[scalarValue.value - 1] : "",
);
const abilityValue = computed<AbilityAnswer>(() => {
  if (props.modelValue && typeof props.modelValue === "object") {
    return props.modelValue as AbilityAnswer;
  }
  return { strong: [], weak: [] };
});

function toggleStrong(option: OptionItem) {
  const current = abilityValue.value;
  const exists = current.strong.some((item) => item.code === option.value);
  let strong: StrongAbility[];
  if (exists) {
    strong = current.strong.filter((item) => item.code !== option.value);
  } else {
    if (current.strong.length >= 5 || current.weak.includes(option.value)) return;
    strong = [...current.strong, { code: option.value, level: 3 }];
  }
  emit("update:modelValue", { ...current, strong });
}

function toggleWeak(option: OptionItem) {
  const current = abilityValue.value;
  const exists = current.weak.includes(option.value);
  let weak: string[];
  if (exists) {
    weak = current.weak.filter((code) => code !== option.value);
  } else {
    if (current.weak.length >= 5 || current.strong.some((item) => item.code === option.value)) return;
    weak = [...current.weak, option.value];
  }
  emit("update:modelValue", { ...current, weak });
}

function updateLevel(code: string, level: number) {
  const current = abilityValue.value;
  emit("update:modelValue", {
    ...current,
    strong: current.strong.map((item) => (item.code === code ? { ...item, level } : item)),
  });
}

function optionName(code: string) {
  return listOptions.value.find((item) => item.value === code)?.label || code;
}

function isStrong(code: string) {
  return abilityValue.value.strong.some((item) => item.code === code);
}

function isWeak(code: string) {
  return abilityValue.value.weak.includes(code);
}

function strongDisabled(code: string) {
  return !isStrong(code) && (isWeak(code) || abilityValue.value.strong.length >= 5);
}

function weakDisabled(code: string) {
  return !isWeak(code) && (isStrong(code) || abilityValue.value.weak.length >= 5);
}
</script>

<template>
  <article class="question-card">
    <div class="question-number">{{ question.sequence.toString().padStart(2, "0") }}</div>
    <h3>{{ question.prompt }}</h3>

    <template v-if="question.kind === 'value_group'">
      <div class="selection-note">已选 {{ selectedValues.length }} / 5</div>
      <van-checkbox-group v-model="selectedValues" :max="5" class="word-grid">
        <van-checkbox
          v-for="option in listOptions"
          :key="option.value"
          :name="option.value"
          shape="square"
        >
          {{ option.label }}
        </van-checkbox>
      </van-checkbox-group>
    </template>

    <template v-else-if="question.kind === 'ability_profile'">
      <div class="ability-block">
        <div class="selection-note">最强项 {{ abilityValue.strong.length }} / 5（选择顺序即排名）</div>
        <div class="ability-grid">
          <button
            v-for="option in listOptions"
            :key="`strong-${option.value}`"
            type="button"
            :class="{ active: isStrong(option.value) }"
            :aria-pressed="isStrong(option.value)"
            :disabled="strongDisabled(option.value)"
            @click="toggleStrong(option)"
          >
            {{ option.label }}
          </button>
        </div>
        <div v-for="(item, index) in abilityValue.strong" :key="item.code" class="level-row">
          <span>{{ index + 1 }}. {{ optionName(item.code) }}</span>
          <select :value="item.level" @change="updateLevel(item.code, Number(($event.target as HTMLSelectElement).value))">
            <option :value="1">知道</option>
            <option :value="2">做过</option>
            <option :value="3">熟练</option>
            <option :value="4">精通</option>
            <option :value="5">创造</option>
          </select>
        </div>
      </div>
      <div class="ability-block">
        <div class="selection-note">待发展项 {{ abilityValue.weak.length }} / 5（选择顺序即排名）</div>
        <div class="ability-grid weak">
          <button
            v-for="option in listOptions"
            :key="`weak-${option.value}`"
            type="button"
            :class="{ active: isWeak(option.value) }"
            :aria-pressed="isWeak(option.value)"
            :disabled="weakDisabled(option.value)"
            @click="toggleWeak(option)"
          >
            {{ option.label }}
          </button>
        </div>
      </div>
    </template>

    <template v-else-if="question.kind === 'reflection_choice'">
      <div class="reflection-options" role="radiogroup" :aria-label="question.prompt">
        <label
          v-for="option in listOptions"
          :key="option.value"
          :class="{ selected: choiceValue === option.value }"
        >
          <input
            v-model="choiceValue"
            type="radio"
            :name="`question-${question.id}`"
            :value="option.value"
          />
          <span>{{ option.label }}</span>
        </label>
      </div>
      <p class="reflection-note">这里没有标准答案，只用于解释前后变化，不影响作答质量。</p>
    </template>

    <template v-else>
      <div v-if="pairOptions?.left && pairOptions?.right" class="pair-labels">
        <span>A · {{ pairOptions.left }}</span>
        <span>B · {{ pairOptions.right }}</span>
      </div>
      <div class="continuum-scale">
        <div
          class="continuum-points"
          role="radiogroup"
          :aria-label="question.prompt"
          :style="{ '--scale-count': scaleLabels.length }"
        >
          <label
            v-for="(label, index) in scaleLabels"
            :key="`${index}-${label}`"
            class="continuum-point"
            :class="{ selected: scalarValue === index + 1 }"
          >
            <input
              v-model="scalarValue"
              type="radio"
              :name="`question-${question.id}`"
              :value="index + 1"
              :aria-label="label"
            />
            <span class="continuum-dot" aria-hidden="true"></span>
          </label>
        </div>
        <div class="continuum-ends" aria-hidden="true">
          <span>{{ scaleStartLabel }}</span>
          <span>{{ scaleEndLabel }}</span>
        </div>
        <p class="continuum-feedback" :class="{ pending: !selectedScaleLabel }" aria-live="polite">
          {{ selectedScaleLabel || "请选择一个最接近的点" }}
        </p>
      </div>
    </template>
  </article>
</template>
